import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * AuditLogModal — shows the change history for a data grid (MTO, Part Rate DB).
 * Reads from a paginated `${endpoint}` that returns { data, total }.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {string} endpoint - e.g. '/mto/audit-log' or '/lt-part-rate-db/audit-log'
 * @param {string} [title='Audit Log']
 * @param {string} [accentColor='#6366f1']
 */
const ACTION_STYLE = {
  create: { bg: '#dcfce7', color: '#166534' },
  update: { bg: '#dbeafe', color: '#1e40af' },
  delete: { bg: '#fee2e2', color: '#991b1b' },
  import: { bg: '#fef9c3', color: '#854d0e' },
};

const PAGE_SIZE = 50;

const AuditLogModal = ({ isOpen, onClose, endpoint, title = 'Audit Log', accentColor = '#6366f1' }) => {
  const { instance, accounts } = useMsal();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchLog = useCallback(async () => {
    if (!isOpen) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((page - 1) * PAGE_SIZE));
      if (search) params.set('search', search);
      const sep = endpoint.includes('?') ? '&' : '?';
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${endpoint}${sep}${params.toString()}`);
      if (r.data?.success) { setRows(r.data.data || []); setTotal(r.data.total || 0); }
    } catch (e) {
      console.error('Failed to load audit log', e);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, endpoint, page, search, isOpen]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  if (!isOpen) return null;

  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const renderDetails = (d) => {
    if (!d) return '—';
    if (Array.isArray(d.changes)) {
      if (d.changes.length === 0) return '—';
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {d.changes.slice(0, 8).map((c, i) => (
            <span key={i} style={{ fontSize: '0.72rem' }}>
              <strong>{c.field}</strong>: <span style={{ color: '#991b1b' }}>{String(c.from ?? '∅')}</span> → <span style={{ color: '#166534' }}>{String(c.to ?? '∅')}</span>
            </span>
          ))}
          {d.changes.length > 8 && <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>+{d.changes.length - 8} more</span>}
        </div>
      );
    }
    if (d.count != null) return `${d.count} rows`;
    if (d.row) return Object.entries(d.row).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}: ${v}`).join(', ') || '—';
    return JSON.stringify(d);
  };

  const th = { padding: '8px 10px', fontSize: '0.68rem', color: '#6b7280', textTransform: 'uppercase', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left', position: 'sticky', top: 0, whiteSpace: 'nowrap' };
  const td = { padding: '6px 10px', fontSize: '0.78rem', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10001 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 12, width: '92%', maxWidth: 1000, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '1rem 1.25rem', background: accentColor, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>📜 {title}</h3>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>✕ Close</button>
        </div>

        <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search action, user, reference, field..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #dee2e6', fontSize: '0.85rem', width: 320, outline: 'none' }} />
          <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{total.toLocaleString()} entries</span>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>When</th>
                <th style={th}>User</th>
                <th style={th}>Action</th>
                <th style={th}>Reference</th>
                <th style={th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>No audit entries yet.</td></tr>
              ) : rows.map((r) => {
                const a = ACTION_STYLE[r.action] || { bg: '#f1f5f9', color: '#475569' };
                return (
                  <tr key={r.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleString()}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.user_email || '—'}</td>
                    <td style={td}><span style={{ backgroundColor: a.bg, color: a.color, padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600 }}>{r.action}</span></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.reference || (r.record_id ? `#${r.record_id}` : '—')}</td>
                    <td style={td}>{renderDetails(r.details)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '0.6rem 1.25rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Page {page} of {pageCount}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #dee2e6', backgroundColor: page <= 1 ? '#f3f4f6' : '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}>‹ Prev</button>
            <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #dee2e6', backgroundColor: page >= pageCount ? '#f3f4f6' : '#fff', cursor: page >= pageCount ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}>Next ›</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogModal;
